import RemoveRedEye from "@mui/icons-material/RemoveRedEye";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { IconButton, TextField } from "@mui/material";
import Grid from "@mui/material/Grid";
import useAppStore from "App/hooks/useAppStore";
import ActionButton from "components/Buttons/ActionButton";
import EmailField from "components/EmailField";
import MfaChallenge from "components/Login/MfaChallenge";
import STATUS_CODE from "enums/STATUS_CODE";
import ROLE from "enums/ROLE_NAME";
import useNotifications from "features/Notifications/hooks/useNotifications";
import usePostAuth from "hooks/api/usePostAuth";
import usePostMfaResend from "hooks/api/usePostMfaResend";
import usePostMfaVerify from "hooks/api/usePostMfaVerify";
import useVidletNavigate from "hooks/navigation/useVidletNavigate";
import { usePersistStore } from "hooks/useStore";
import { useState } from "react";
import AuthDto from "types/AuthDto";
import MfaChallengeDto from "types/MfaChallengeDto";
import isEmail from "utils/validators/isEmail";

function postLoginPath(auth: AuthDto, redirectUrl: string) {
  if (auth.role === ROLE.ACCOUNT_SEARCHER) {
    return redirectUrl.startsWith("/search") ? redirectUrl : "/search";
  }
  return redirectUrl;
}

export default function Login() {
  const { postAuth, isWriting } = usePostAuth();
  const { postMfaVerify, isWriting: isMfaVerifying } = usePostMfaVerify();
  const { postMfaResend, isWriting: isMfaResending } = usePostMfaResend();
  const { navigate } = useVidletNavigate();
  const { handlePSChangeAll, setMfaDeviceToken, mfaDeviceTokens } =
    usePersistStore();
  const { handleAppChange } = useAppStore();
  const { setNotification } = useNotifications();

  const [email, setEmail] = useState<string | undefined>();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // MFA challenge state
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallengeDto | null>(
    null,
  );
  const [mfaCode, setMfaCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);

  function exitMfa() {
    setMfaChallenge(null);
    setMfaCode("");
    setRememberDevice(false);
  }

  const queryParams = new URLSearchParams(window.location.search);
  const redirect_url = queryParams.get("redirect_url") || "/";

  function completeSession(auth: AuthDto) {
    if (!email) return;
    handlePSChangeAll({ token: auth.token });
    handleAppChange("currentUser", auth);
    if (auth.deviceToken) {
      setMfaDeviceToken(email, auth.deviceToken);
    }
    navigate(postLoginPath(auth, redirect_url), {
      state: window.location.pathname,
    });
  }

  async function getToken() {
    if (!email) return;
    try {
      const deviceToken = mfaDeviceTokens?.[email];
      const result = await postAuth({
        email,
        password,
        deviceToken,
      });

      if (result.kind === "authenticated") {
        completeSession(result.data);
      } else if (result.kind === "mfa_required") {
        setMfaChallenge(result.mfa);
        setMfaCode("");
        setRememberDevice(false);
      }
    } catch (e) {
      console.error(e);
    }
  }

  if (mfaChallenge) {
    return (
      <Grid container direction="column" spacing={1}>
        <Grid size={{ xs: 12 }} sx={{ mb: 1 }}>
          <p style={{ margin: 0, color: "text.secondary" }}>
            A verification code was sent to {email}.
          </p>
        </Grid>
        <MfaChallenge
          code={mfaCode}
          rememberDevice={rememberDevice}
          isWriting={isMfaVerifying || isMfaResending}
          onCodeChange={setMfaCode}
          onRememberDeviceChange={setRememberDevice}
          onSubmit={async () => {
            if (!email) return;
            try {
              const result = await postMfaVerify({
                challengeToken: mfaChallenge.challengeToken,
                code: mfaCode,
                rememberDevice,
              });

              setMfaChallenge(null);
              completeSession(result);
            } catch (error) {
              if (
                (error as { response?: { status?: number } }).response
                  ?.status === STATUS_CODE.UNAUTHORIZED
              ) {
                setNotification({
                  message: "Invalid verification code. Please try again.",
                  severity: "error",
                });
              }
            }
          }}
          onResend={async () => {
            try {
              const result = await postMfaResend({
                challengeToken: mfaChallenge.challengeToken,
              });
              setMfaChallenge(result);
            } catch (error) {
              const status = (error as { response?: { status?: number } })
                .response?.status;
              if (status === 404 || status === 410) {
                setNotification({
                  message: "Verification expired. Please sign in again.",
                  severity: "error",
                });
                exitMfa();
              }
            }
          }}
        />
        <Grid size={{ xs: 12 }}>
          <ActionButton fullWidth onClick={exitMfa}>
            Back to sign in
          </ActionButton>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container direction="column" spacing={1}>
      <form name="login">
        <Grid size={{ xs: 12 }}>
          <EmailField
            fullWidth
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Grid>

        <Grid size={{ xs: 12 }} sx={{ mb: 2, mt: 2 }}>
          <TextField
            label="Password"
            type={showPassword ? "text" : "password"}
            autoComplete="password"
            fullWidth
            required
            value={password}
            slotProps={{
              input: {
                endAdornment: (
                  <IconButton
                    size="small"
                    onClick={() => setShowPassword(!showPassword)}
                    title={showPassword ? "hide password" : "show password"}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <RemoveRedEye />}
                  </IconButton>
                ),
              },
            }}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              (e.code === "Enter" || e.key === "Enter") && getToken();
            }}
          />
        </Grid>
      </form>

      <Grid size={{ xs: 12 }}>
        <ActionButton
          fullWidth
          onClick={getToken}
          disabled={
            !email ||
            !password ||
            !isEmail(email) ||
            isWriting ||
            isMfaVerifying ||
            isMfaResending
          }
        >
          Sign In
        </ActionButton>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <ActionButton fullWidth onClick={() => navigate("/forgot-password")}>
          Forgot your password?
        </ActionButton>
      </Grid>
    </Grid>
  );
}
